import { Link } from 'react-router';

import { EpisodeCard } from '@/components/serie/EpisodeCard';
import { Thumb } from '@/components/serie/Thumb';
import { Accent } from '@/components/ui/Accent';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { Container } from '@/components/ui/Container';
import { Label } from '@/components/ui/Label';
import { Section } from '@/components/ui/Section';
import { episodePath, episodes, findPhase, latestEpisode, phaseEpisodes, phases } from '@/content/serie';
import { site, socialHref, socials } from '@/content/site';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const FORMATS = [
  { label: 'La vidéo', title: '5 à 10 minutes', body: "Capture brute de la démo, intégrée dans l'article. Elle prouve que ça tourne." },
  { label: "L'article", title: "Ce qu'on mesure, ce qui casse", body: 'Le format carottage, daté, avec un verdict en trois lignes.' },
  { label: 'Le code', title: 'Un repo par phase', body: "Workflows n8n, prompts et scripts de l'épisode, sur GitHub." },
];

/** Réseaux mis en avant pour suivre la série. */
const FOLLOW = ['youtube', 'linkedin', 'instagram'];

export function SeriePage() {
  useDocumentMeta({
    title: `La série, ${site.name}`,
    description:
      "Chaque épisode ajoute un outil au pipeline de veille, le fait tourner devant la caméra, mesure ce qu'il apporte et se termine par un verdict.",
    path: '/serie',
  });

  const latestPhase = latestEpisode && findPhase(latestEpisode.phase);
  const github = socials.find((social) => social.id === 'github');

  return (
    <>
      <section aria-labelledby="serie-titre" className="pt-13 pb-7 tab:pt-21 tab:pb-12">
        <Container>
          <Label>La série vidéo</Label>
          <h1 id="serie-titre" className="mt-5.5 text-title">
            Un projet,
            <br />
            <Accent text={`${numberWord(episodes.length)} |épisodes.`} />
          </h1>
          <p className="mt-4.5 max-w-[40em] text-sub text-encre-soft">
            Chaque épisode ajoute un outil au pipeline de veille, le fait tourner devant la caméra,
            mesure ce qu&apos;il apporte et se termine par un verdict. Capture d&apos;écran brute,
            sans montage.
          </p>
        </Container>
      </section>

      <Section className="pt-0 tab:pt-0" aria-label="Dernier épisode">
        <Container>
          {latestEpisode && (
            <div className="grid items-center gap-6 desk:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] desk:gap-10">
              <Link to={episodePath(latestEpisode)} className="group sans-lien" aria-label={`Regarder l'épisode ${latestEpisode.num}`}>
                <Thumb num={latestEpisode.num} minutes={latestEpisode.videoMinutes} large />
              </Link>
              <div>
                <Label>Dernier épisode, phase {latestPhase?.num}</Label>
                <h2 className="mt-3.5 text-h3-lg">{latestEpisode.title}</h2>
                {latestEpisode.summary && (
                  <p className="mt-3.5 text-encre-soft">{latestEpisode.summary}</p>
                )}
                <div className="mt-6 flex flex-wrap gap-2.5">
                  <ButtonLink to={episodePath(latestEpisode)} icon="play">
                    Regarder
                  </ButtonLink>
                  <ButtonLink to={episodePath(latestEpisode)} variant="ligne" icon="doc">
                    Lire l&apos;article
                  </ButtonLink>
                  {github && (
                    <ButtonLink to={socialHref(github)} variant="ligne" icon="github">
                      Le code
                    </ButtonLink>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-14 grid gap-5 desk:grid-cols-3">
            {FORMATS.map((format) => (
              <div key={format.label} className="border-t border-encre pt-3.5">
                <Label>{format.label}</Label>
                <b className="mt-2 mb-1.5 block text-h6 font-semibold">{format.title}</b>
                <p className="text-body-xs text-encre-soft">{format.body}</p>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      <Section className="pt-0 tab:pt-0" aria-label="Tous les épisodes">
        <Container>
          {phases.map((phase) => (
            <div key={phase.id}>
              <div className="mb-6 flex items-baseline gap-4 border-b border-line pt-14 pb-5">
                <Label>Phase {phase.num}</Label>
                <h2 className="text-h3">{phase.title}</h2>
                <p className="ml-auto hidden text-body-xs text-encre-soft desk:block">{phase.gain}</p>
              </div>
              <div className="grid gap-x-6 gap-y-7 tab:grid-cols-2 desk:grid-cols-3">
                {phaseEpisodes(phase.id).map((episode) => (
                  <EpisodeCard key={episode.slug} episode={episode} />
                ))}
              </div>
            </div>
          ))}
        </Container>
      </Section>

      <Section tone="encre" aria-labelledby="suivre-titre">
        <Container className="flex flex-wrap items-center justify-between gap-8">
          <div>
            <Label tone="clair">Un épisode par mois</Label>
            <h2 id="suivre-titre" className="mt-4 text-h2">
              Ne ratez pas
              <br />
              <Accent text="le |prochain." variant="ciel" />
            </h2>
          </div>
          <div className="flex flex-wrap gap-2.5">
            {socials
              .filter((social) => FOLLOW.includes(social.id))
              .map((social) => (
                <ButtonLink key={social.id} to={socialHref(social)} variant="ciel" icon={social.icon}>
                  {social.label}
                </ButtonLink>
              ))}
          </div>
        </Container>
      </Section>
    </>
  );
}

const WORDS = ['zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix', 'onze', 'douze'];

/** « onze » plutôt que « 11 » dans le titre ; au-delà de douze, le chiffre. */
const numberWord = (n: number) => WORDS[n] ?? String(n);
